import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { ComponentNameTranslationPipe } from '@twaice-fe/frontend/shared/services';
import * as echarts from 'echarts';
import { SeriesOption } from 'echarts';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { AxisSettings, GlobalSettings, TimeRangeInterface } from '@twaice-fe/shared/models';
import { FileDownloadService } from '@twaice-fe/frontend/shared/services';
import { DataPointDE, SensorTypeFormatterService } from '@twaice-fe/frontend/shared/utilities';
import { ChartDeService } from './chart-de.service';

const DOUBLECLICK_THRESHOLD_MS = 300;

@Component({
  selector: 'twaice-fe-shared-chart-de',
  templateUrl: './chart-de.component.html',
  styleUrls: ['./chart-de.component.scss'],
  providers: [ComponentNameTranslationPipe, SensorTypeFormatterService, ChartDeService, FileDownloadService],
  imports: [CommonModule, NzEmptyModule],
})
export class ChartDeComponent implements OnDestroy, AfterViewInit, OnChanges {
  @Input() dataPoints: DataPointDE[] | null = [];
  @Input() timeRange: TimeRangeInterface | null = null;
  @Input() globalSettings: GlobalSettings = { chartMode: 'single', groupBy: 'unit', dataType: 'cleansed' };
  @Input() axisSettings: AxisSettings[] = [];
  @Input() userTimezone: string | null = null;
  @Input() systemTimezone: string | null = null;
  @Input() showLegend = true;
  @Input() legendOrient: 'horizontal' | 'vertical' | null = null;
  @Input() showToolbox = true;

  @ViewChildren('chartContainer') chartContainers: QueryList<ElementRef>;
  charts: echarts.ECharts[] = [];
  emptyCharts: boolean[] = [];
  chartNumber = 0;

  axis: AxisSettings[];

  private isPanActiveValue = true;
  private resizeObserver?: ResizeObserver[] = [];
  private sensorForChart: Record<
    string,
    {
      data: [Date, number][];
      key: string;
      displayName: string;
      unit: string;
      valueType: string;
      computationType?: string;
      sensorType?: string;
      scalingFactor?: number;
    }
  > = {};

  private lastLegendEnabledItem: string | null = null;
  private lastLegendEnabledTimestampMS = 0;
  private lastLegendEnabledChartIndex = -1;

  private lastLegendDisabledItem: string | null = null;
  private lastLegendDisabledTimestampMS = 0;
  private lastLegendDisabledChartIndex = -1;

  constructor(
    private componentNameTranslationPipe: ComponentNameTranslationPipe,
    private chartService: ChartDeService,
    private fileDownloadService: FileDownloadService
  ) {}

  get isPanActive(): boolean {
    return this.isPanActiveValue;
  }

  set isPanActive(value: boolean) {
    this.isPanActiveValue = value;
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Shift') {
      this.setChartsZoomLock(false);
    }
  }

  @HostListener('window:keyup', ['$event'])
  onKeyUp(event: KeyboardEvent) {
    if (event.key === 'Shift') {
      this.setChartsZoomLock(true);
    }
  }

  ngOnDestroy(): void {
    if (this.charts) {
      this.charts.forEach((chart) => chart.dispose());
    }

    if (this.resizeObserver) {
      this.resizeObserver.forEach((observer) => observer.disconnect());
    }
  }

  ngAfterViewInit(): void {
    if (this.chartContainers?.length > 0) {
      this.initChartVariables();
      if (this.globalSettings && this.axis && this.timeRange) {
        this.initChart(this.globalSettings, this.axis, this.timeRange);
      }
    }

    this.chartContainers.changes.subscribe(() => {
      this.initChartVariables();
      this.initChart(this.globalSettings, this.axis, this.timeRange);
    });
  }

  ngOnChanges(): void {
    this.chartService.setTimezoneContext({
      userTimezone: this.userTimezone,
      systemTimezone: this.systemTimezone,
    });
    if (this.timeRange && this.globalSettings && this.axisSettings?.length) {
      this.handleDataUpdate(this.dataPoints, this.timeRange, this.globalSettings, this.axisSettings);
    }
  }

  private handleDataUpdate(
    dataPoints: DataPointDE[] | null,
    timeRange: TimeRangeInterface,
    globalSettings: GlobalSettings,
    axis: AxisSettings[]
  ): void {
    // Clear chart when data is null (reset) or process new data
    if (!dataPoints) {
      this.sensorForChart = {};
    } else {
      this.processSensorData(dataPoints);
    }

    this.updateComponentState(globalSettings, axis, timeRange);
    this.scheduleChartInitialization(globalSettings, axis, timeRange);
  }

  private processSensorData(dataPoints: any[]): void {
    this.sensorForChart = dataPoints.reduce(
      (acc, dataPoint) => {
        if (!this.isValidDataPoint(dataPoint)) {
          console.warn('Invalid data point structure', dataPoint);
          return acc;
        }

        const sensorKey = this.generateSensorKey(dataPoint);
        const sortedData = this.sortDataPointsByTimestamp(dataPoint.data || []);

        acc[sensorKey] = {
          key: dataPoint.sensor.key,
          displayName: this.generateSensorDisplayName(dataPoint),
          data: sortedData,
          unit: dataPoint.sensor.unit,
          computationType: dataPoint.sensor.computationType,
          valueType: dataPoint.sensor.valueType,
          sensorType: dataPoint.sensor.sensorType,
          scalingFactor: dataPoint.sensor.scalingFactor,
        };
        return acc;
      },
      {} as Record<string, any>
    );
  }

  private isValidDataPoint(dataPoint: any): boolean {
    return dataPoint?.sensor?.name && dataPoint?.componentBk;
  }

  // Series are keyed by the raw componentBk so that components sharing a custom name
  // (e.g. "String1" in several racks) remain separate series; the custom name is only
  // used for what the user sees (legend, tooltip, CSV header).
  private generateSensorKey(dataPoint: any): string {
    return `${this.getSensorName(dataPoint)} (${dataPoint.componentBk})`;
  }

  private generateSensorDisplayName(dataPoint: any): string {
    return `${this.getSensorName(dataPoint)} (${this.componentNameTranslationPipe.transform(dataPoint.componentBk)})`;
  }

  private getSensorName(dataPoint: any): string {
    const sensorName = dataPoint.sensor.name;
    return dataPoint.sensor.computationType === 'computation' ? `${sensorName} TWAICE` : sensorName;
  }

  private sortDataPointsByTimestamp(data: [Date, number][]): [Date, number][] {
    return [...data].sort((a, b) => {
      const timeA = a[0] instanceof Date ? a[0].getTime() : a[0];
      const timeB = b[0] instanceof Date ? b[0].getTime() : b[0];
      return timeA - timeB;
    });
  }

  private updateComponentState(globalSettings: GlobalSettings, axis: AxisSettings[], timeRange: TimeRangeInterface): void {
    this.globalSettings = globalSettings;
    this.axis = axis;
    this.timeRange = timeRange;
    this.chartNumber = this.calculateChartNumber(globalSettings, axis);
  }

  private calculateChartNumber(globalSettings: GlobalSettings, axis: AxisSettings[]): number {
    return globalSettings.chartMode === 'single' ? 1 : axis.length || 1;
  }

  private initChartVariables(reset: boolean = false) {
    if (this.charts.length !== this.chartContainers.length || reset) {
      this.charts.forEach((chart) => chart.dispose());
      this.charts = [];

      if (this.emptyCharts.length !== this.chartNumber) {
        this.emptyCharts = Array(this.chartNumber).fill(false);
      }

      for (let i = 0; i < this.chartContainers.length; i++) {
        this.charts.push(echarts.init(this.chartContainers.get(i).nativeElement));
      }

      const groupId = 'syncedCharts';
      this.charts.forEach((chart) => (chart.group = groupId));
      echarts.connect(groupId);

      this.resizeObserver.forEach((observer) => observer.disconnect());
      this.resizeObserver = [];
    }
  }

  private downloadCSV(
    sensors: Record<string, { data: [Date, number][]; displayName: string; unit: string; computationType?: string }>
  ): void {
    if (!sensors || Object.keys(sensors).length === 0) {
      console.warn('No data available to download');
      return;
    }

    if (!this.charts.length) {
      console.warn('Chart not initialized');
      return;
    }

    // Get the currently selected series from the chart
    const selectedSeries = this.charts
      .map((chart) => chart.getOption().legend[0].selected)
      .reduce((acc, series) => ({
        ...acc,
        ...series,
      }));

    // Filter sensor names to only include those that are selected in the legend
    const allSensorNames = Object.keys(sensors);
    const sensorNames = allSensorNames.filter(
      (name) => selectedSeries === undefined || selectedSeries[name] === undefined || selectedSeries[name] === true
    );

    if (sensorNames.length === 0) {
      console.warn('No sensors selected');
      return;
    }

    // Create CSV header with timezone name
    const header = [
      `Timestamp (${this.chartService.getTimezoneName()})`,
      ...sensorNames.map((name) => sensors[name].displayName ?? name),
    ].map((field) => this.chartService.escapeCsvHeaderField(field));

    // Create a map of timestamps to organize data points
    const timestampMap = new Map<string, { [key: string]: number | null }>();

    // Collect all timestamps and values for selected sensors only
    sensorNames.forEach((sensorName) => {
      const sensorData = sensors[sensorName];
      sensorData.data.forEach((dataPoint) => {
        if (Array.isArray(dataPoint) && dataPoint.length === 2) {
          const [timestamp, value] = dataPoint;

          // Convert timestamp to ISO string with correct timezone offset per data point
          const timestampStr = this.chartService.formatCsvTimestamp(new Date(timestamp));

          if (!timestampMap.has(timestampStr)) {
            timestampMap.set(timestampStr, {});
          }
          const timestampDataPoint = timestampMap.get(timestampStr);
          if (timestampDataPoint) {
            timestampDataPoint[sensorName] = value;
          }
        } else {
          console.warn('Unsupported data point format', dataPoint);
        }
      });
    });

    // Sort timestamps
    const sortedTimestamps = Array.from(timestampMap.keys()).sort();

    // Create CSV rows
    const rows = sortedTimestamps.map((timestamp) => {
      const values = sensorNames.map((name) => {
        const dataPoint = timestampMap.get(timestamp);
        return dataPoint && dataPoint[name] !== undefined ? dataPoint[name] : '';
      });
      return [timestamp, ...values];
    });

    // Combine header and rows
    const csvContent = [header.join(','), ...rows.map((row) => row.join(','))].join('\n');

    // Use shared service for download
    this.fileDownloadService.triggerClientDownload(csvContent, {
      fileName: `sensor-data-${this.chartService.formatCsvDate(new Date())}.csv`,
      fileType: 'text/csv;charset=utf-8',
    });
  }

  private scheduleChartInitialization(globalSettings: GlobalSettings, axis: AxisSettings[], timeRange: TimeRangeInterface): void {
    setTimeout(() => {
      this.initChart(globalSettings, axis, timeRange);
    });
  }

  private updatePanStateFromChart(): void {
    this.isPanActiveValue = true;
  }

  private initChart(globalSettings: GlobalSettings, axis: AxisSettings[], timeRange?: TimeRangeInterface | null): void {
    const sensors = this.sensorForChart;

    if (!globalSettings || !axis || axis.length === 0) {
      console.warn('Invalid chart configuration', { globalSettings, axis });
      return;
    }

    if (!this.chartContainers || this.chartContainers.length !== this.chartNumber) {
      console.warn('ChartContainers not yet initialized', {
        chartContainersLength: this.chartContainers?.length,
        chartNumber: this.chartNumber,
      });
      return;
    }

    for (let chartIndex = 0; chartIndex < this.chartNumber; chartIndex++) {
      try {
        const container = this.chartContainers.get(chartIndex);
        if (!container?.nativeElement) {
          console.warn(`Chart container ${chartIndex} not found`);
          continue;
        }

        const axisCount = axis.length;
        const axisSpacing = 80;
        const emptyLineColors = ['#5470C6', '#91CC75', '#FAC858', '#EE6666', '#73C0DE', '#3BA272', '#FC8452', '#9A60B4'];
        const lineColors = ['#5470C6', '#91CC75', '#FAC858', '#EE6666', '#73C0DE', '#3BA272', '#FC8452', '#9A60B4'];

        const chartData = this.chartService.processChartData(
          sensors,
          axis,
          globalSettings,
          timeRange,
          this.chartNumber,
          chartIndex
        );

        const option = this.chartService.createChartOption({
          hasData: chartData.hasData,
          filteredSensors: chartData.filteredSensors,
          filteredAxis: chartData.filteredAxis,
          globalSettings,
          axisCount,
          axisSpacing,
          timeRange,
          minDate: chartData.minDate,
          maxDate: chartData.maxDate,
          unitMinMax: chartData.unitMinMax,
          chartIndex,
          emptyLineColors,
          lineColors,
          sensors,
          showLegend: this.showLegend,
          legendOrient: this.legendOrient,
          showToolbox: this.showToolbox,
        });

        if (chartData.hasData && option.toolbox && this.showToolbox) {
          option.toolbox = this.chartService.createToolboxConfig(
            chartIndex,
            this.chartService.createYAxes(
              chartData.filteredAxis,
              globalSettings,
              axisCount,
              axisSpacing,
              chartData.unitMinMax,
              chartIndex,
              chartData.filteredSensors
            ),
            {
              onSingleItemTool: (index) => this.handleSingleItemTool(index),
              onPanIndicator: (index) => this.handlePanIndicator(index),
              onResetTool: (index) => this.handleResetTool(index),
              onDownloadCSV: () => this.downloadCSV(this.sensorForChart),
            }
          );
        }

        this.charts[chartIndex].setOption(option);
        this.emptyCharts[chartIndex] = !chartData.hasData;

        this.setupChartEventHandlers(chartIndex, chartData.hasData, chartData.filteredSensors, sensors, globalSettings);
      } catch (error) {
        console.error(`Error initializing chart ${chartIndex}:`, error);
      }
    }
  }

  private setupChartEventHandlers(
    chartIndex: number,
    hasData: boolean,
    filteredSensors: [string, any][],
    sensors: Record<string, any>,
    globalSettings: GlobalSettings
  ) {
    // Legend select changed handler
    this.charts[chartIndex].off('legendselectchanged');
    this.charts[chartIndex].on('legendselectchanged', (params: { name: string }) => {
      const name = params.name;
      const sensor = hasData ? this.sensorForChart[name] : sensors[name];
      const currentOption = this.charts[chartIndex].getOption();

      if (!currentOption.legend[0].data.some((key) => key === name)) {
        // event is for a different chart
        return;
      }

      if (currentOption.legend[0].selected[name]) {
        // item enabled -> check if it's a toggle
        if (this.lastLegendEnabledItem === name && this.lastLegendEnabledChartIndex === chartIndex) {
          // double toggle (on -> off -> on) was detected
          if (new Date().getTime() - this.lastLegendEnabledTimestampMS < DOUBLECLICK_THRESHOLD_MS) {
            // It's considered a double click if below threshold
            const notAllOtherDisabled = Object.entries(currentOption.legend[0].selected)
              .filter(([key, value]) => key !== name)
              .some(([key, value]) => value === true);

            if (notAllOtherDisabled) {
              // There are some entries enabled -> disable all others
              Object.keys(currentOption.legend[0].selected).forEach((key) => {
                currentOption.legend[0].selected[key] = key === name;
              });
            } else {
              // Everything was already disabled -> enable all
              Object.keys(currentOption.legend[0].selected).forEach((key) => {
                currentOption.legend[0].selected[key] = true;
              });
            }
          }
          this.lastLegendEnabledItem = null;
          this.lastLegendEnabledChartIndex = -1;
        }

        // item enable -> remember name and timestamp
        this.lastLegendDisabledItem = name;
        this.lastLegendDisabledTimestampMS = new Date().getTime();
        this.lastLegendDisabledChartIndex = chartIndex;
      } else {
        // item disabled -> check if it was a double toggle
        if (this.lastLegendDisabledItem === name && this.lastLegendDisabledChartIndex === chartIndex) {
          // double toggle (off -> on -> off) was detected
          if (new Date().getTime() - this.lastLegendDisabledTimestampMS < DOUBLECLICK_THRESHOLD_MS) {
            // It's considered a double click if below threshold -> enable all
            Object.keys(currentOption.legend[0].selected).forEach((key) => {
              currentOption.legend[0].selected[key] = true;
            });
          }
          this.lastLegendDisabledItem = null;
          this.lastLegendDisabledChartIndex = -1;
        }

        // item disabled -> remember name and timestamp
        this.lastLegendEnabledItem = name;
        this.lastLegendEnabledTimestampMS = new Date().getTime();
        this.lastLegendEnabledChartIndex = chartIndex;
      }

      if (currentOption.legend?.[0]?.selected) {
        if (!sensor || !sensor.data || sensor.data.length === 0) {
          currentOption.legend[0].selected[name] = false;
        }
      }
      this.charts[chartIndex]?.setOption(currentOption);
    });

    if (hasData) {
      // Initialize pan state
      this.updatePanStateFromChart();

      // Get the current option to access the series for event handling
      const currentOption = this.charts[chartIndex].getOption();
      const dataSeries = currentOption.series as SeriesOption[];

      // Mouse hover handlers for symbols
      this.charts[chartIndex].on('mouseover', { seriesIndex: 'all' }, (params: any) => {
        this.charts[chartIndex].setOption({
          series: dataSeries.map((series, index) => ({
            id: series.name,
            showSymbol: index === params.seriesIndex,
          })),
        });
      });

      this.charts[chartIndex].on('mouseout', { seriesIndex: 'all' }, () => {
        this.charts[chartIndex].setOption({
          series: dataSeries.map((series) => ({
            id: series.name,
            showSymbol: false,
          })),
        });
      });

      // Synchronized zoom and pan for stacked charts
      if (globalSettings.chartMode !== 'single' && this.chartNumber > 1) {
        this.charts[chartIndex].on('datazoom', (params: any) => {
          if (params.batch) {
            params.batch.forEach((batchItem: any) => {
              this.chartService.syncZoomAcrossCharts(this.charts, chartIndex);
            });
          } else {
            this.chartService.syncZoomAcrossCharts(this.charts, chartIndex);
          }
        });

        this.charts[chartIndex].on('globalcursortaken', (params: any) => {
          if (params.key === 'dataZoomSelect') {
            this.isPanActive = !params.dataZoomSelectActive;
          }
        });

        this.charts[chartIndex].on('mousewheel', (params: any) => {
          if (params.event && params.event.shiftKey) {
            this.chartService.syncMouseWheelZoom(this.charts, chartIndex);
          }
        });
      }

      // Pan state update handler
      this.charts[chartIndex].on('globalcursortaken', (params: any) => {
        const areaZoomActive = params?.key === 'dataZoomSelect' && params?.dataZoomSelectActive;
        this.isPanActive = !areaZoomActive;
        const toolboxFeature = currentOption.toolbox?.[0]?.feature || {};
        const panBlue = '#3E98C5';
        const panGrey = '#6e6e6e';
        const updatedConfig = {
          iconStyle: {
            color: this.isPanActive ? panBlue : panGrey,
            borderColor: this.isPanActive ? panBlue : panGrey,
          },
          emphasis: {
            iconStyle: {
              color: panBlue,
              borderColor: panBlue,
            },
          },
        };
        this.charts[chartIndex].setOption({
          toolbox: {
            feature: {
              ...toolboxFeature,
              myPanIndicator: { ...updatedConfig },
            },
          },
        });
      });
    }

    // Resize observer
    this.resizeObserver.push(
      new ResizeObserver(() => {
        if (this.charts[chartIndex]) {
          this.charts[chartIndex].resize();
        }
      })
    );
    this.resizeObserver[this.resizeObserver.length - 1].observe(this.chartContainers.get(chartIndex).nativeElement);
  }

  private handleSingleItemTool(chartIndex: number): void {
    if (this.charts[chartIndex]) {
      const oldOption = this.charts[chartIndex].getOption();

      this.charts[chartIndex].setOption({
        tooltip: {
          trigger: oldOption.tooltip[0].trigger === 'axis' ? 'item' : 'axis',
        },
        toolbox: {
          feature: {
            mySingleItemTool: {
              iconStyle: {
                borderColor: oldOption.tooltip[0].trigger === 'axis' ? '#66A3FF' : '#6e6e6e',
              },
            },
          },
        },
      });
    }
  }

  private handlePanIndicator(chartIndex: number): void {
    if (this.charts[chartIndex]) {
      this.isPanActive = !this.isPanActive;

      if (this.isPanActive) {
        this.charts[chartIndex].dispatchAction({
          type: 'takeGlobalCursor',
          key: 'dataZoomSelect',
          dataZoomSelectActive: false,
        });
      } else {
        this.charts[chartIndex].dispatchAction({
          type: 'takeGlobalCursor',
          key: 'dataZoomSelect',
          dataZoomSelectActive: true,
        });
      }

      const currentOption = this.charts[chartIndex].getOption();
      const toolboxFeature = currentOption.toolbox?.[0]?.feature || {};
      const panBlue = '#3E98C5';
      const panGrey = '#6e6e6e';
      const updatedConfig = {
        iconStyle: {
          color: this.isPanActive ? panBlue : panGrey,
          borderColor: this.isPanActive ? panBlue : panGrey,
        },
        emphasis: {
          iconStyle: {
            color: panBlue,
            borderColor: panBlue,
          },
        },
      };
      this.charts[chartIndex].setOption({
        toolbox: {
          feature: {
            ...toolboxFeature,
            myPanIndicator: { ...updatedConfig },
          },
        },
      });
    }
  }

  private handleResetTool(chartIndex: number): void {
    if (this.charts[chartIndex]) {
      const currentOption = this.charts[chartIndex].getOption();
      const selectedSeries = currentOption.legend[0].selected;

      this.initChartVariables(true);
      this.initChart(this.globalSettings, this.axis, this.timeRange);

      if (selectedSeries) {
        const newOption = this.charts[chartIndex].getOption();
        newOption.legend[0].selected = selectedSeries;
        this.charts[chartIndex].setOption({
          legend: {
            selected: selectedSeries,
          },
        });
      }
    }
  }

  private setChartsZoomLock(zoomLock: boolean) {
    if (!this.charts) return;
    for (const chart of this.charts) {
      const option = chart.getOption();
      if (option.dataZoom && Array.isArray(option.dataZoom)) {
        const newDataZoom = option.dataZoom.map((dz: any) => {
          if (dz.type === 'inside') {
            return { ...dz, zoomLock, zoomOnMouseWheel: 'shift' };
          }
          return dz;
        });
        chart.setOption({ dataZoom: newDataZoom }, false);
      }
    }
  }
}
